<template>
  <div v-loading="loading" class="edit-payment-dialog">
    <div class="edit-payment-dialog__content">
      <div class="edit-payment-dialog__title">Edit Payment</div>
      <div>
        <div class="form-input" :class="{ 'form-input--full': form.payment_date }">
          <SingleDatePicker
            :title="'Payment Date'"
            style="--datepicker-width: 100%"
            :disabled="fininceFieldsDisabled"
            :value="form.payment_date ? new Date(form.payment_date) : null"
            @changeDates="onChangePaymentDate"
          />
        </div>
        <FormInput
          label="Payment Amount"
          v-model="form.payment_amount"
          :disabled="fininceFieldsDisabled"
          type="number"
        />

        <FormInput label="Budget" v-model="form.budget" :disabled="fininceFieldsDisabled" type="number" />

        <FormInput
          label="Agent Commission"
          v-model="form.agent_commission"
          :disabled="fininceFieldsDisabled"
          type="number"
        />

        <FormInput
          label="Transaction Commission"
          v-model="form.transaction_commission"
          :disabled="fininceFieldsDisabled"
          type="number"
        />

        <div class="form-input">
          <SingleDatePicker
            :title="'Invoice Date'"
            style="--datepicker-width: 100%"
            :disabled="invoiceFieldsDisabled"
            :value="form.invoice_date ? new Date(form.invoice_date) : null"
            @changeDates="onChangeInvoiceDate"
          />
        </div>

        <div class="form-input">
          <SelectorNew
            title="Status"
            :disabled="invoiceFieldsDisabled"
            :options="statusOptions"
            v-model="form.status"
            style="--selector-width: 100%"
          />
        </div>

        <FormInput label="Invoice Currency" v-model="form.invoice_currency" :disabled="true" type="text" />

        <FormInput label="Invoice Number" v-model="form.invoice_number" :disabled="true" type="text" />

        <FormInput
          label="Invoice Amount"
          v-model="form.invoice_amount"
          :disabled="true"
          type="number"
        />

        <div class="edit-payment-dialog__controls">
          <ButtonElement text="Save" view="empty" @click-button="savePayment" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SingleDatePicker from '@/components/sort/SingleDatePicker.vue'
import ButtonElement from '@/components/elements/ButtonElement'
import { updatePayment } from '../../api/mutations'
import { getDateFromString } from '../../utils/date'
import FormInput from '../inputs/FormInput.vue'
import SelectorNew from '../sort/SelectorNew.vue'

const statusOptions = [
  { id: 'pending', name: 'Pending' },
  { id: 'paid', name: 'Paid' },
  { id: 'partially_paid', name: 'Partially Paid' },
  { id: 'overdue', name: 'Overdue' },
  { id: 'cancelled', name: 'Cancelled' }
]

export default {
  name: 'EditPaymentDialog',
  components: {
    SingleDatePicker,
    SelectorNew,
    ButtonElement,
    FormInput
  },
  data() {
    return {
      form: {
        payment_date: '',
        payment_amount: '',
        budget: '',
        agent_commission: '',
        transaction_commission: '',
        status: '',
        invoice_currency: '',
        invoice_number: '',
        invoice_date: '',
        invoice_amount: ''
      },
      loading: false,
      statusOptions
    }
  },
  computed: {
    ...mapState('Dialog', ['dialogProps']),
    fininceFieldsDisabled() {
      return !this.dialogProps?.chosenPayment?.can_edit_finance_fields
    },
    invoiceFieldsDisabled() {
      return !this.dialogProps?.chosenPayment?.can_edit_invoice_fields
    }
  },
  watch: {
    'dialogProps.chosenPayment': {
      handler(val) {
        if (val) {
          const status = val.status
          this.form.status = val.status
          const newForm = {
            payment_date: getDateFromString(val.payment_date),
            payment_amount: val.payment_amount,
            budget: val.budget,
            agent_commission: val.agent_commission,
            transaction_commission: val.transaction_commission,
            status: status,
            invoice_currency: val.invoice_currency,
            invoice_number: val.invoice_number ? '' + val.invoice_number : '',
            invoice_date: getDateFromString(val.invoice_date),
            invoice_amount: val.invoice_amount
          }
          this.form = newForm
        }
      },
      immediate: true
    }
  },
  methods: {
    onChangePaymentDate(val) {
      if (val && val.startDate) {
        this.form.payment_date = this.toInputDate(val.startDate)
      } else {
        this.form.payment_date = ''
      }
    },
    onChangeInvoiceDate(val) {
      if (val && val.startDate) {
        this.form.invoice_date = this.toInputDate(val.startDate)
      } else {
        this.form.invoice_date = ''
      }
    },
    toInputDate(date) {
      if (!date) return ''
      if (typeof date === 'string') return date.slice(0, 10)
      const d = new Date(date)
      return d.toISOString().slice(0, 10)
    },
    async savePayment() {
      this.loading = true

      try {
        const payload = {
          ...this.form,
          invoice_date: this.toInputDate(this.form.invoice_date),
          payment_date: this.toInputDate(this.form.payment_date)
        }
        await updatePayment({ id: this.dialogProps.chosenPayment.id, data: payload })
        this.dialogProps.updatePayment(payload)
        this.closeDialog()
      } catch (e) {
        console.error('Failed to save payment, ', e)
      } finally {
        this.loading = false
      }
    },
    closeDialog() {
      this.$store.commit('Dialog/setData', { label: 'dialogVisible', data: false })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.edit-payment-dialog {
  &__title {
    font-weight: 700;
    font-size: 3rem;
    line-height: 3.5rem;
    margin-bottom: 3rem;
    color: $basicBlack;
  }

  &__content {
    background: #fff;
    color: #333;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    padding: 8rem 8.5rem;
  }

  &__controls {
    margin-top: 2.4rem;
  }
}
</style>
